import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Award, FolderOpen, Images, Lock, TriangleAlert, Volume2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { hasDraft, useDraft } from '../lib/persist'
import type { EventSettings } from '../lib/types'
import { applyCertificateThreshold, applySoundCheckin } from '../lib/event-config'
import { useAuthed } from '../lib/session'
import { Masthead } from '../ui/Chrome'
import { Button } from '../ui/Button'
import { Field } from '../ui/Field'
import { Skeleton } from '../ui/Feedback'
import { useToast } from '../ui/Feedback'

/**
 * Checked here as well as on the server, so a typo is caught before a round trip
 * rather than coming back as a generic rejection. The server is still the authority;
 * this is only to make the error land next to the field it belongs to.
 */
function linkProblem(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed.length > 500) return 'That address is too long.'
  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'Use an http:// or https:// address.'
    }
    return null
  } catch {
    return 'That is not a full web address — include https://'
  }
}

/*
 * What a null threshold means, spelled once. An event with no `setting_apps` row gates
 * at 100 — nobody is reported eligible on the strength of a row that does not exist —
 * so that is the number the field opens on, not a blank.
 */
const DEFAULT_THRESHOLD = 100

/**
 * Whole percent, 0 to 100. The column is a float, so 70.5 would store — and then
 * `/event/current` would round it to 71 and gate at 70.5, a threshold that disagrees
 * with itself. 0 is allowed and means everybody qualifies.
 */
function thresholdProblem(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'Enter a percentage.'
  if (!/^\d{1,3}$/.test(trimmed)) return 'Use a whole number, with no decimal point.'
  const n = Number(trimmed)
  if (n > 100) return 'A percentage cannot be above 100.'
  return null
}

export function VenueSettingsRoute() {
  const session = useAuthed()
  const toast = useToast()

  const [settings, setSettings] = useState<EventSettings | null>(null)
  /* Drafts: a reload mid-edit (the tab discarded while checking a figure elsewhere)
     brings the typed value back instead of the server's. Cleared on save. */
  const [gallery, setGallery, clearGallery] = useDraft('settings.gallery', '')
  const [resources, setResources, clearResources] = useDraft('settings.resources', '')
  const [attendance, setAttendance, clearAttendance] = useDraft('settings.attendance', '')
  const [attendanceError, setAttendanceError] = useState<string | null>(null)
  const [threshold, setThreshold, clearThreshold] = useDraft('settings.threshold', '')
  const [saving, setSaving] = useState(false)
  const [savingLinks, setSavingLinks] = useState(false)
  const [savingThreshold, setSavingThreshold] = useState(false)
  const [serverFault, setServerFault] = useState(false)
  const [galleryError, setGalleryError] = useState<string | null>(null)
  const [resourcesError, setResourcesError] = useState<string | null>(null)
  const [thresholdError, setThresholdError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getSettings(session.email, session.at)
      .then((next) => {
        if (cancelled) return
        setSettings(next)
        if (!hasDraft('settings.gallery')) setGallery(next.gallery_url ?? '')
        if (!hasDraft('settings.resources')) setResources(next.resources_url ?? '')
        if (!hasDraft('settings.attendance')) setAttendance(next.attendance_url ?? '')
        /*
         * Empty is not a threshold. A null one means the event has no settings row, and
         * the app reads that as 100 — so the field opens on 100 rather than blank, and
         * saving it writes the row that was missing.
         */
        if (!hasDraft('settings.threshold')) setThreshold(String(next.certificate_threshold ?? DEFAULT_THRESHOLD))
      })
      .catch(() => {
        if (!cancelled) {
          setSettings({
            show_cert: false, gallery_url: null, resources_url: null, certificate_threshold: null, sound_checkin: false, names_locked: false, attendance_link: false, attendance_url: null, attendance_on: false,
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [session])

  function describe(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.status === 403) {
      return 'This account is not allowed to change event settings'
    }
    if (err instanceof ApiError && err.status === 400) {
      return 'The server would not accept that address'
    }
    return fallback
  }

  const toggle = useCallback(async () => {
    if (!settings || saving) return
    const next = !settings.show_cert

    // Optimistic: the switch is the whole interaction, so it should move at once.
    setSettings({ ...settings, show_cert: next })
    setSaving(true)
    try {
      // Links deliberately not sent: the server leaves out what it is not given, so
      // the toggle cannot overwrite a half-typed address sitting in the fields below.
      await api.updateSettings(session.email, session.at, next)
      setServerFault(false)
      toast.show(next ? 'Certificates are now visible' : 'Certificates hidden', 'ok')
    } catch (err) {
      setSettings({ ...settings, show_cert: !next })
      // Was a standing 500: the handler read an `id_event` it never declared and threw
      // before reaching the update. Fixed on the server, so a 5xx here is now an
      // ordinary fault worth retrying rather than a known dead end.
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected that change'), 'bad')
    } finally {
      setSaving(false)
    }
  }, [settings, saving, session, toast])

  /*
   * Same shape as the certificate toggle. Off closes the path on the server as well
   * as hiding the buttons: /checkIN refuses a sound_code while this is off.
   */
  const toggleSound = useCallback(async () => {
    if (!settings || saving) return
    const next = !settings.sound_checkin
    setSettings({ ...settings, sound_checkin: next })
    setSaving(true)
    try {
      await api.updateSettings(session.email, session.at, settings.show_cert, { sound_checkin: next })
      applySoundCheckin(next)
      setServerFault(false)
      toast.show(next ? 'Sound check-in is on' : 'Sound check-in is off', 'ok')
    } catch (err) {
      setSettings({ ...settings, sound_checkin: !next })
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected that change'), 'bad')
    } finally {
      setSaving(false)
    }
  }, [settings, saving, session, toast])

  /*
   * The PDF batch runs once, so names must stop moving before it. This freezes every
   * participant's certificate name at once; a certificate already issued freezes its
   * own holder regardless. Enforced by the server; the client only greys the button.
   */
  /* The tile switch. Independent of the address, so it can be held back until the
     form should be filled, then flipped without retyping. */
  const toggleAttendance = useCallback(async () => {
    if (!settings || saving) return
    const next = !settings.attendance_on
    setSettings({ ...settings, attendance_on: next, attendance_link: next && Boolean(settings.attendance_url) })
    setSaving(true)
    try {
      await api.updateSettings(session.email, session.at, settings.show_cert, { attendance_on: next })
      setServerFault(false)
      toast.show(next ? 'Attendance tile is on' : 'Attendance tile is off', 'ok')
    } catch (err) {
      setSettings({ ...settings, attendance_on: !next })
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected that change'), 'bad')
    } finally {
      setSaving(false)
    }
  }, [settings, saving, session, toast])

  const toggleNamesLocked = useCallback(async () => {
    if (!settings || saving) return
    const next = !settings.names_locked
    setSettings({ ...settings, names_locked: next })
    setSaving(true)
    try {
      await api.updateSettings(session.email, session.at, settings.show_cert, { names_locked: next })
      setServerFault(false)
      toast.show(next ? 'Certificate names are locked' : 'Certificate names can be edited', 'ok')
    } catch (err) {
      setSettings({ ...settings, names_locked: !next })
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected that change'), 'bad')
    } finally {
      setSaving(false)
    }
  }, [settings, saving, session, toast])

  async function saveLinks(event: FormEvent) {
    event.preventDefault()
    if (!settings || savingLinks) return

    const gProblem = linkProblem(gallery)
    const rProblem = linkProblem(resources)
    const aProblem = linkProblem(attendance)
    setGalleryError(gProblem)
    setResourcesError(rProblem)
    setAttendanceError(aProblem)
    if (gProblem || rProblem || aProblem) return

    setSavingLinks(true)
    try {
      await api.updateSettings(session.email, session.at, settings.show_cert, {
        gallery_url: gallery.trim(),
        resources_url: resources.trim(),
        attendance_url: attendance.trim(),
      })
      setSettings({
        ...settings,
        gallery_url: gallery.trim() || null,
        resources_url: resources.trim() || null,
        attendance_url: attendance.trim() || null,
        attendance_link: Boolean(attendance.trim()),
      })
      setServerFault(false)
      toast.show('Links saved', 'ok')
      clearGallery(); setGallery(gallery.trim())
      clearResources(); setResources(resources.trim())
      clearAttendance(); setAttendance(attendance.trim())
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected those links'), 'bad')
    } finally {
      setSavingLinks(false)
    }
  }

  async function saveThreshold(event: FormEvent) {
    event.preventDefault()
    if (!settings || savingThreshold) return

    const problem = thresholdProblem(threshold)
    setThresholdError(problem)
    if (problem) return

    const percent = Number(threshold.trim())
    setSavingThreshold(true)
    try {
      await api.updateSettings(session.email, session.at, settings.show_cert, {
        certificate_threshold: percent,
      })
      setSettings({ ...settings, certificate_threshold: percent })
      /*
       * Keeps the boot-resolved config in step with what was just saved. Nothing on an
       * organiser's own screens reads it — Schedule.tsx is the only live consumer and
       * that is a participant screen — so this is for demo mode, where one browser is
       * both roles, and for whatever reads the module next. Delegates pick the figure up
       * on their next load; the hint below says so.
       */
      applyCertificateThreshold(percent)
      clearThreshold()
      setThreshold(String(percent))
      setServerFault(false)
      toast.show(`Threshold saved — ${percent}% of required sessions`, 'ok')
    } catch (err) {
      if (err instanceof ApiError && err.status >= 500) setServerFault(true)
      toast.show(describe(err, 'The server rejected that threshold'), 'bad')
    } finally {
      setSavingThreshold(false)
    }
  }

  const thresholdDirty =
    settings !== null
    && threshold.trim() !== String(settings.certificate_threshold ?? DEFAULT_THRESHOLD)

  const linksDirty =
    settings !== null
    && (gallery.trim() !== (settings.gallery_url ?? '')
      || resources.trim() !== (settings.resources_url ?? '')
      || attendance.trim() !== (settings.attendance_url ?? ''))

  return (
    <main className="page">
      <Masthead kicker="Organiser" title="Settings" />

      {settings === null ? (
        <Skeleton height="4rem" style={{ marginTop: 'var(--space-lg)' }} />
      ) : (
        <div className="rows">
          <button
            className="row"
            onClick={() => void toggle()}
            disabled={saving}
            aria-describedby="cert-help"
          >
            <span className="row__label">
              <Award size={17} strokeWidth={1.4} />
              Release certificates
            </span>
            <span
              className="switch"
              role="switch"
              aria-checked={settings.show_cert}
              aria-label="Release certificates"
            />
          </button>
          <button
            className="row"
            onClick={() => void toggleSound()}
            disabled={saving}
            aria-describedby="sound-help"
          >
            <span className="row__label">
              <Volume2 size={17} strokeWidth={1.4} />
              Sound check-in
            </span>
            <span
              className="switch"
              role="switch"
              aria-checked={settings.sound_checkin}
              aria-label="Sound check-in"
            />
          </button>
          <button
            className="row"
            onClick={() => void toggleNamesLocked()}
            disabled={saving}
            aria-describedby="names-help"
          >
            <span className="row__label">
              <Lock size={17} strokeWidth={1.4} />
              Lock certificate names
            </span>
            <span
              className="switch"
              role="switch"
              aria-checked={settings.names_locked}
              aria-label="Lock certificate names"
            />
          </button>
        </div>
      )}

      <p id="cert-help" className="muted" style={{ fontSize: 'var(--step--1)', paddingTop: 'var(--space-md)' }}>
        When this is on, participants who have met the attendance threshold can see and
        open their certificate. It applies to this event only.
      </p>
      <p id="sound-help" className="muted" style={{ fontSize: 'var(--step--1)', paddingTop: 'var(--space-xs)' }}>
        Experimental. When on, the venue screen can broadcast a rotating code through
        the room speakers and delegates get a Listen button beside the camera. The QR
        code keeps working either way.
      </p>
      <p id="names-help" className="muted" style={{ fontSize: 'var(--step--1)', paddingTop: 'var(--space-xs)' }}>
        Switch this on before generating the certificates. Participants can no longer
        edit the name printed on theirs; anyone whose certificate is already issued is
        locked either way. First-time name confirmation still works.
      </p>

      {settings !== null ? (
        <form className="settings-links" onSubmit={(event) => void saveThreshold(event)}>
          <h2 className="settings-links__title display">Attendance threshold</h2>
          <p className="muted settings-links__intro">
            How much of their track a delegate has to attend before a certificate is
            theirs. It counts only the sessions marked as needing a check-in.
          </p>

          <Field
            label="Percentage required"
            type="text"
            inputMode="numeric"
            value={threshold}
            error={thresholdError}
            hint={
              settings.certificate_threshold === null
                ? 'This event has no settings row yet, so it is gating at 100%. Saving creates it.'
                : 'Delegates see the new figure the next time they open the app.'
            }
            onChange={(event) => { setThreshold(event.target.value); setThresholdError(null) }}
            autoComplete="off"
            spellCheck={false}
          />

          <div className="settings-links__actions">
            <Button type="submit" variant="primary" busy={savingThreshold} disabled={!thresholdDirty}>
              Save threshold
            </Button>
          </div>
        </form>
      ) : null}

      {settings !== null ? (
        <form className="settings-links" onSubmit={(event) => void saveLinks(event)}>
          <h2 className="settings-links__title display">Gallery and resources</h2>
          <p className="muted settings-links__intro">
            Paste the addresses participants should be sent to. Leave one blank and its
            tile keeps saying the feature is coming, rather than linking nowhere.
          </p>

          {/*
            * type="text", not type="url". A url input runs the browser's own constraint
            * validation on submit, which blocks the form before onSubmit fires - so
            * linkProblem() never ran and the reader got a native tooltip instead of the
            * error beneath the field. inputMode still brings up the right keyboard.
            */}
          <Field
            label="Gallery"
            type="text"
            inputMode="url"
            placeholder="https://photos.app.goo.gl/…"
            hint="Event photos — a shared album, or wherever they are published."
            value={gallery}
            error={galleryError}
            onChange={(event) => { setGallery(event.target.value); setGalleryError(null) }}
            autoComplete="off"
            spellCheck={false}
          />

          <Field
            label="Resources"
            type="text"
            inputMode="url"
            placeholder="https://drive.google.com/…"
            hint="Slides, handouts and the programme file."
            value={resources}
            error={resourcesError}
            onChange={(event) => { setResources(event.target.value); setResourcesError(null) }}
            autoComplete="off"
            spellCheck={false}
          />

          {/* The switch sits on the input's row, right-aligned, so address and tile
              read as one setting. It is a sibling of the Field, not inside it: a
              button inside a <label> would toggle on every click into the input. */}
          <div className="settings-links__inline">
            <Field
              label="UiTM attendance form"
              type="text"
              inputMode="url"
              placeholder="https://attendance.uitm.edu.my/index_event.php?id=…"
              hint={settings.attendance_url
                ? 'ePendaftaran for the course. Delegates get a tile that opens it in a new tab, during the event, while the switch is on. The address is never printed in the app.'
                : 'ePendaftaran for the course. Save an address, then switch the tile on.'}
              value={attendance}
              error={attendanceError}
              onChange={(event) => { setAttendance(event.target.value); setAttendanceError(null) }}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className="settings-links__toggle"
              onClick={() => void toggleAttendance()}
              disabled={saving || !settings.attendance_url}
              aria-label={settings.attendance_on ? 'Hide the attendance tile' : 'Show the attendance tile'}
              title={settings.attendance_on ? 'Tile is on' : 'Tile is off'}
            >
              <span className="kicker">Show</span>
              <span className="switch" role="switch" aria-checked={Boolean(settings.attendance_on)} />
            </button>
          </div>

          <div className="settings-links__actions">
            <Button type="submit" variant="primary" busy={savingLinks} disabled={!linksDirty}>
              Save links
            </Button>
            {settings.gallery_url || settings.resources_url ? (
              <p className="settings-links__live">
                {settings.gallery_url ? (
                  <span><Images size={13} strokeWidth={1.6} /> Gallery is live</span>
                ) : null}
                {settings.resources_url ? (
                  <span><FolderOpen size={13} strokeWidth={1.6} /> Resources is live</span>
                ) : null}
              </p>
            ) : null}
          </div>
        </form>
      ) : null}

      {serverFault ? (
        <div
          className="modebanner"
          style={{
            marginTop: 'var(--space-lg)',
            marginInline: 'calc(-1 * var(--gutter))',
            borderTop: '1px solid color-mix(in srgb, var(--warn) 30%, transparent)',
            alignItems: 'flex-start',
          }}
          role="alert"
        >
          <TriangleAlert size={15} strokeWidth={1.6} style={{ flex: 'none', marginTop: 2 }} />
          <span>
            <strong>That did not save.</strong> The server returned an error, so the
            setting is unchanged and the switch has been put back. Try again in a moment.
          </span>
        </div>
      ) : null}
    </main>
  )
}
